import { ENV } from '../config/env';

const BASE = ENV.AI_API_BASE;
if (!BASE) console.warn('REACT_APP_AI_SERVER_URL not set');

/**
 * Verify liveness. Never throws and never hangs past timeoutMs.
 * Returns the server response fields plus `outcome`: NO_VIOLATION when status === 'success',
 * VIOLATION for a valid non-success verdict (student must adjust), otherwise an AI_* / NETWORK_ERROR value.
 */
export async function verifyLiveness(imageBase64, { timeoutMs = 20000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${BASE}/verify-liveness`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: imageBase64 }),
      signal: controller.signal,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) return { status: 'error', outcome: outcomeForHttpStatus(res.status), httpStatus: res.status };
    if (!data || typeof data !== 'object' || !data.status) return { status: 'error', outcome: AI_OUTCOME.INVALID_AI_RESPONSE };
    return { ...data, outcome: data.status === 'success' ? AI_OUTCOME.NO_VIOLATION : AI_OUTCOME.VIOLATION };
  } catch (err) {
    const timedOut = err?.name === 'AbortError' || err?.name === 'TimeoutError';
    return { status: 'error', outcome: timedOut ? AI_OUTCOME.AI_TIMEOUT : AI_OUTCOME.NETWORK_ERROR };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Monitoring outcomes. Only VIOLATION may be counted/recorded/reported as a violation;
 * every other non-NO_VIOLATION value means the AI could not give a verdict.
 */
export const AI_OUTCOME = Object.freeze({
  VIOLATION: 'VIOLATION',
  NO_VIOLATION: 'NO_VIOLATION',
  AI_ERROR: 'AI_ERROR',
  AI_TIMEOUT: 'AI_TIMEOUT',
  AI_UNAVAILABLE: 'AI_UNAVAILABLE',
  AI_RATE_LIMIT: 'AI_RATE_LIMIT',
  NETWORK_ERROR: 'NETWORK_ERROR',
  INVALID_AI_RESPONSE: 'INVALID_AI_RESPONSE',
});

const VIOLATION_WARNING_TYPES = new Set([
  'no_face', 'multiple_faces', 'person_not_recognized', 'different_person', 'spoof_detected', 'face_turned_away',
]);

function outcomeForHttpStatus(status) {
  if (status === 429) return AI_OUTCOME.AI_RATE_LIMIT;
  if (status === 502 || status === 503 || status === 504) return AI_OUTCOME.AI_UNAVAILABLE;
  if (status === 408) return AI_OUTCOME.AI_TIMEOUT;
  return AI_OUTCOME.AI_ERROR;
}

/**
 * Verify exam monitoring (reference vs current image).
 * Never throws: returns the server response fields plus a normalized `outcome` (see AI_OUTCOME).
 */
export async function verifyExamMonitoring(payload, signal) {
  let res;
  try {
    res = await fetch(`${BASE}/verify-exam-monitoring`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: signal || undefined,
    });
  } catch (err) {
    const timedOut = err?.name === 'TimeoutError' || err?.name === 'AbortError';
    return {
      status: 'error',
      outcome: timedOut ? AI_OUTCOME.AI_TIMEOUT : AI_OUTCOME.NETWORK_ERROR,
      message: timedOut ? 'Monitoring request timed out' : 'Monitoring service unreachable',
    };
  }

  const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
  const data = contentType.includes('application/json') ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    return { ...(data || {}), status: 'error', outcome: outcomeForHttpStatus(res.status), httpStatus: res.status, message: data?.message || `HTTP ${res.status}` };
  }
  if (!data || typeof data !== 'object') {
    return { status: 'error', outcome: AI_OUTCOME.INVALID_AI_RESPONSE, message: 'Invalid monitoring response' };
  }
  if (data.status === 'success') return { ...data, outcome: AI_OUTCOME.NO_VIOLATION };
  if (data.status === 'fail') {
    const type = String(data.warning_type || '').trim().toLowerCase();
    return { ...data, outcome: VIOLATION_WARNING_TYPES.has(type) ? AI_OUTCOME.VIOLATION : AI_OUTCOME.AI_ERROR };
  }
  return { ...data, status: 'error', outcome: AI_OUTCOME.AI_ERROR };
}

export function getAiApiBase() {
  return BASE;
}
